import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Code2, History, Loader2, RotateCcw, Database, Clapperboard } from "lucide-react";
import TopBar from "../components/workspace/TopBar";
import ChatPanel from "../components/workspace/ChatPanel";
import PreviewPane from "../components/workspace/PreviewPane";
import CodePane from "../components/workspace/CodePane";
import VersionsPane from "../components/workspace/VersionsPane";
import DatabasePane from "../components/workspace/DatabasePane";
import VideoPane from "../components/video/VideoPane";
import ErrorBanner from "../components/workspace/ErrorBanner";
import PublishDialog from "../components/workspace/PublishDialog";
import ShareDialog from "../components/workspace/ShareDialog";
import GithubDialog from "../components/workspace/GithubDialog";
import { useWorkspace } from "../hooks/useWorkspace";
import { useAutoFix } from "../hooks/useAutoFix";
import { useCredits } from "../context/CreditsContext";
import { api, errMsg, downloadBlob } from "../lib/api";
import LowBalanceBar from "../components/LowBalanceBar";
import DomainRenewalBar from "../components/DomainRenewalBar";
import { FAST_MODEL } from "../components/workspace/SettingsSheet";

const TABS = [["preview", "Preview", Eye], ["code", "Code", Code2], ["database", "Database", Database], ["versions", "Versions", History], ["video", "Video", Clapperboard]];

const OldVersionBanner = ({ v, onBack, onRestore }) => (
  <div className="flex items-center gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-700" data-testid="old-version-banner">
    Viewing version {v.number} (read-only)
    <button onClick={onRestore} data-testid="old-version-restore" className="ml-auto flex items-center gap-1 hover:text-slate-900"><RotateCcw size={12} /> Restore</button>
    <button onClick={onBack} data-testid="old-version-back" className="hover:text-slate-900">Back to latest</button>
  </div>
);

export default function Workspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { project, setProject, version, messages, building, stopping, send, stop, loadProject, error } = useWorkspace(id);
  const { costOf } = useCredits();
  const [params] = useSearchParams();
  const openVideo = params.get("tab") === "video";
  const [tab, setTab] = useState(openVideo ? "video" : "preview");
  const [model, setModel] = useState(null);
  const [fast, setFastState] = useState(() => localStorage.getItem(`ishukart:fast:${id}`) === "1");
  const setFast = (v) => { setFastState(v); localStorage.setItem(`ishukart:fast:${id}`, v ? "1" : "0"); };
  const [mobilePanel, setMobilePanel] = useState(openVideo ? "preview" : "chat");
  const [viewing, setViewing] = useState(null);
  const [dialog, setDialog] = useState(null);

  const onFix = useCallback((text) => send(text, model, { kind: "autofix", mode: "build" }), [send, model]);
  const fix = useAutoFix({ version, viewing, building, messages, enabled: project?.auto_fix !== false, onFix });

  useEffect(() => { if (project && !model) setModel(project.model); }, [project, model]);
  useEffect(() => { setViewing(null); }, [version?.id]);

  if (error) return <div className="h-screen flex flex-col items-center justify-center gap-3 bg-[#F5F8FC] text-slate-500" data-testid="workspace-error">{error}<Link to="/home" className="text-emerald-600">Back to projects</Link></div>;
  if (!project) return <div className="h-screen flex items-center justify-center bg-[#F5F8FC]"><Loader2 className="animate-spin text-emerald-600" /></div>;

  const shown = viewing || version;
  const previewVersion = async (vid) => {
    if (vid === version?.id) setViewing(null);
    else { try { setViewing(await api.version(project.id, vid)); } catch (e) { toast.error(errMsg(e)); } }
    setTab("preview");
    setMobilePanel("preview");
  };
  const restoreViewing = async () => {
    try { await api.restoreVersion(project.id, viewing.id); toast.success(`Restored version ${viewing.number}`); await loadProject(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rename = async (name) => { try { setProject(await api.renameProject(project.id, name)); } catch (e) { toast.error(errMsg(e)); } };
  const settings = async (body) => { try { setProject(await api.projectSettings(project.id, body)); } catch (e) { toast.error(errMsg(e)); } };
  const zip = async () => { try { toast.success(`Downloaded ${downloadBlob(await api.exportZip(project.id), "app.zip")}`); } catch { toast.error("Download failed"); } };
  const duplicate = async (v) => {
    try {
      const p = await api.duplicateProject(project.id, v?.id);
      toast.success(`Created “${p.name}”`);
      navigate(`/project/${p.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const banner = viewing && <OldVersionBanner v={viewing} onBack={() => setViewing(null)} onRestore={restoreViewing} />;
  const errorBanner = (
    <ErrorBanner errors={fix.errors} status={fix.status} autoFix={project.auto_fix !== false} cost={costOf(model, false, false)}
      onToggleAutoFix={(v) => settings({ auto_fix: v })} onFixNow={fix.fixNow} onDismiss={fix.reset} />
  );

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-[#F5F8FC]" data-testid="workspace-page">
      <TopBar project={project} building={building} onRename={rename} onZip={zip} onGithub={() => setDialog("github")} onPublish={() => setDialog("publish")}
        onShare={() => setDialog("share")} onDuplicate={() => duplicate()} mobilePanel={mobilePanel} setMobilePanel={setMobilePanel} />
      <LowBalanceBar />
      <DomainRenewalBar />
      <div className="flex-1 min-h-0 flex">
        <section className={`${mobilePanel === "chat" ? "flex" : "hidden"} lg:flex w-full lg:w-[420px] xl:w-[460px] shrink-0 flex-col border-r border-slate-200 bg-white`}>
          <ChatPanel messages={messages} busy={building} stopping={stopping} onSend={(t, opts) => send(t, fast ? FAST_MODEL : model, opts)} onStop={stop} model={model} setModel={setModel}
            onPreviewVersion={previewVersion} alwaysPlan={!!project.always_plan} onAlwaysPlan={(v) => settings({ always_plan: v })}
            fast={fast} setFast={setFast} hasVersion={!!project.current_version_id} previewPillDesktop={tab !== "preview"}
            onViewPreview={() => { setTab("preview"); setMobilePanel("preview"); }} onGithub={() => setDialog("github")} onFork={() => duplicate()} />
        </section>
        <section className={`${mobilePanel === "preview" ? "flex" : "hidden"} lg:flex flex-1 min-w-0 flex-col bg-slate-50`}>
          <div className="h-11 border-b border-slate-200 px-2 flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {TABS.map(([tid, label, Icon]) => (
              <button key={tid} onClick={() => setTab(tid)} data-testid={`workspace-tab-${tid}`}
                aria-label={label} className={`h-8 px-2.5 sm:px-3 rounded-lg text-xs flex items-center gap-1.5 transition-colors shrink-0 ${tab === tid ? "bg-slate-100 text-slate-900" : "text-slate-500 hover:text-slate-900 hover:bg-slate-100"}`}>
                <Icon size={13} /> <span className={tab === tid ? "" : "hidden sm:inline"}>{label}</span>
                {tid === "preview" && fix.errors.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />}
              </button>
            ))}
          </div>
          <div className="flex-1 min-h-0">
            {tab === "preview" && <PreviewPane version={shown} building={building} banner={banner} errorBanner={errorBanner} onError={fix.addError} onReset={fix.reset} />}
            {tab === "code" && <CodePane projectId={project.id} version={shown} readOnly={!!viewing || building} onSaved={loadProject} />}
            {tab === "database" && <DatabasePane project={project} />}
            {tab === "video" && <VideoPane project={project} initialVideo={params.get("video")} />}
            {tab === "versions" && <VersionsPane project={project} onPreview={previewVersion} onRestored={loadProject} onCopy={duplicate} refreshKey={project.current_version_id} />}
          </div>
        </section>
      </div>
      <PublishDialog open={dialog === "publish"} onOpenChange={(o) => !o && setDialog(null)} project={project} onChange={setProject} />
      <ShareDialog open={dialog === "share"} onOpenChange={(o) => !o && setDialog(null)} project={project} onChange={setProject} />
      <GithubDialog open={dialog === "github"} onOpenChange={(o) => !o && setDialog(null)} project={project} onChange={setProject} />
    </div>
  );
}
